import { ObjectTypes } from '../../shared/enums';
import { TeeExhaustRectangular, TeeExhaustRound, TeeSupplyRectangular, TeeSupplyRound } from "../../libs/hydraulic-resistance";
import { createTeeLines } from "../../shared/utils/lineFactory";
import { s } from "../../shared/globalState/settings";
import { Point } from "../Point";
import { Vector } from "../Vector";
import { getPointByVectorAndLength, getMiddleVector, getMiddlePoint, transformPointByMatrix3 } from "../../shared/common";
import { findLinesIntersection, isPointInsideFrame } from "../../shared/common";
import { Block } from "../../models/shapes/Block";
import { Line } from "../../models/shapes/Line";
import { mat3 } from 'gl-matrix';
import { RouteCrucialPoints, FlowDirection } from '../../shared/types';
import { AbstractFrame } from '../frames/AbstractFrame';
import { SelectBoundary } from '../frames/SelectBoundary';



export class Tee extends Block {

    shape: string;
    exhaustOrSupply: string;
    passOrTurn: string;

    // размеры
    widthCollectorMillimeter: number;
    heightCollectorMillimeter: number;
    widthPassMillimeter: number;
    heightPassMillimeter: number;
    widthTurnMillimeter: number;
    heightTurnMillimeter: number;

    // расход
    flowRateCollectorCmh: number;
    flowRateTurnCmh: number;

    // угол
    angleDegree: number;

    // геометрическая форма
    _center: Point | null = null;
    sideLength: number = 12;
    _connectedSegStart: Point | null = null;
    _connectedSegEnd: Point | null = null;
    flangeLength: number = 10;
    vecStart: Vector | null = null;
    vecEnd: Vector | null = null;
    start: Point | null = null;
    end: Point | null = null;
    _segmentBase: Line | null = null;
    _segmentCurrent: Line | null = null;
    _base1: Point | null = null;
    _base2: Point | null = null;
    _side: Point | null = null;

    // тип
    type = ObjectTypes.tee;



    // цвет
    color: number[] = [0.5, 0, 0.5, 1];
    teeType: string = ObjectTypes.simpleTee;

    // Flow direction type per branch: Map<branchId, 'on pass' | 'on turn' | 'UNKNOWN'>
    // The same tee can have different flowDirectionType in different branches
    flowDirectionType: Map<string, 'on pass' | 'on turn' | 'UNKNOWN'> = new Map();
    
    // DP and KMS per branch: Map<branchId, { dp: number, kms: number }>
    // The same tee can have different DP and KMS in different branches
    branchDp: Map<string, number> = new Map();
    branchKms: Map<string, number> = new Map();

    get center(): Point {
        if (!this._center) {
            throw new Error('## Center point is not set');
        }
        return this._center;
    }
    set center(point: Point) {
        this._center = point;
        this.updateGeometry();
    }

    get connectedSegStart() {
        if (!this._connectedSegStart) {
            throw new Error('## Connected segment start point is not set');
        }
        return this._connectedSegStart;
    }
    set connectedSegStart(point: Point) {
        this._connectedSegStart = point;
        this.updateGeometry();
    }

    get connectedSegEnd() {
        if (!this._connectedSegEnd) {
            throw new Error('## Connected segment end point is not set');
        }
        return this._connectedSegEnd;
    }
    set connectedSegEnd(point: Point) {
        this._connectedSegEnd = point;
        this.updateGeometry();
    }

    get base1() {
        if (!this._base1) {
            throw new Error('## Base 1 point is not set');
        }
        return this._base1;
    }
    set base1(point: Point) {
        this._base1 = point;
        this.updateGeometry();
    }
    get base2() {
        if (!this._base2) {
            throw new Error('## Base 2 point is not set');
        }
        return this._base2;
    }
    set base2(point: Point) {
        this._base2 = point;
        this.updateGeometry();
    }
    get side() {
        if (!this._side) {
            throw new Error('## Side point is not set');
        }
        return this._side;
    }
    set side(point: Point) {
        this._side = point;
        this.updateGeometry();
    }


    constructor(
        shape: string,
        exhaustOrSupply: string,
        passOrTurn: string,
        widthCollectorMillimeter: number,
        heightCollectorMillimeter: number,
        widthPassMillimeter: number,
        heightPassMillimeter: number,
        widthTurnMillimeter: number,
        heightTurnMillimeter: number,
        flowRateCollectorCmh: number,
        flowRateTurnCmh: number,
        angleDegree: number, center: Point | null = null, segmentBase: Line | null = null, segmentCurrent: Line | null = null) {


        var color = s.teeColor;
        var thickness = s.blockThickness;

        var shapes = generateShapes(s.teeType);

        super(shapes, color, thickness);

        if (!segmentBase) {
            throw new Error('## Segment base is not set');
        }
        if (!segmentCurrent) {
            throw new Error('## Segment current is not set');
        }
        if (!center) {
            throw new Error('## Center point is not set');
        }
        var vecBase = segmentBase.getVector(center);
        var vecCurrent = segmentCurrent.getVector(center);

        var basePoint1 = getPointByVectorAndLength(vecBase, this.sideLength);
        var basePoint2 = getPointByVectorAndLength(vecBase, -this.sideLength);
        var currentPoint = getPointByVectorAndLength(vecCurrent, this.sideLength);

        this.base1 = new Point(
            basePoint1.x + center.x,
            basePoint1.y + center.y
        );
        this.base2 = new Point(
            basePoint2.x + center.x,
            basePoint2.y + center.y
        );
        this.side = new Point(
            currentPoint.x + center.x,
            currentPoint.y + center.y
        );

        this.shape = shape;
        this.exhaustOrSupply = exhaustOrSupply;
        this.passOrTurn = passOrTurn;

        this.widthCollectorMillimeter = widthCollectorMillimeter;
        this.heightCollectorMillimeter = heightCollectorMillimeter;
        this.widthPassMillimeter = widthPassMillimeter;
        this.heightPassMillimeter = heightPassMillimeter;
        this.widthTurnMillimeter = widthTurnMillimeter;
        this.heightTurnMillimeter = heightTurnMillimeter;

        this.flowRateCollectorCmh = flowRateCollectorCmh;
        this.flowRateTurnCmh = flowRateTurnCmh;

        this.angleDegree = angleDegree;

    }

    updateGeometry() {

        if (this._center && this._connectedSegStart && this.connectedSegEnd) {
            this.vecStart = new Vector(this._center, this._connectedSegStart);
            this.vecEnd = new Vector(this._center, this._connectedSegEnd);

            var start = getPointByVectorAndLength(this.vecStart, this.sideLength);
            var end = getPointByVectorAndLength(this.vecEnd, this.sideLength);

            var startX = this.center.x - start.x;
            var startY = this.center.y - start.y;
            var endX = this.center.x - end.x;
            var endY = this.center.y - end.y;

            this.start = new Point(startX, startY);
            this.end = new Point(endX, endY);

            if (this.teeType === ObjectTypes.simpleTee) {
                var passSegment = this.shapes[0] as Line;
                var sideSegment = this.shapes[1] as Line;
                var flange1Segment = this.shapes[2] as Line;
                var flange2Segment = this.shapes[3] as Line;
                var flange3Segment = this.shapes[4] as Line;

                passSegment.start = this.base1;
                passSegment.end = this.base2;

                var flange1Vec = this.vecStart.getPerpendicularVectorAtPoint(this.start);
                var flange1VecOpposite = this.vecStart.getOppositeToPerpendicularVectorAtPoint(this.start);
                var flange2Vec = this.vecEnd.getPerpendicularVectorAtPoint(this.end);
                var flange2VecOpposite = this.vecEnd.getOppositeToPerpendicularVectorAtPoint(this.end);


                var flange1StartPoint = getPointByVectorAndLength(flange1VecOpposite, this.flangeLength / 2);
                flange1Segment.start = new Point(
                    flange1Vec.start.x + flange1StartPoint.x,
                    flange1Vec.start.y + flange1StartPoint.y
                );

                var flange1EndPoint = getPointByVectorAndLength(flange1Vec, this.flangeLength / 2);
                flange1Segment.end = new Point(
                    flange1VecOpposite.start.x + flange1EndPoint.x,
                    flange1VecOpposite.start.y + flange1EndPoint.y
                );

                var flange2StartPoint = getPointByVectorAndLength(flange2VecOpposite, this.flangeLength / 2);
                flange2Segment.start = new Point(
                    flange2Vec.start.x + flange2StartPoint.x,
                    flange2Vec.start.y + flange2StartPoint.y
                );

                var flange2EndPoint = getPointByVectorAndLength(flange2Vec, this.flangeLength / 2);
                flange2Segment.end = new Point(
                    flange2VecOpposite.start.x + flange2EndPoint.x,
                    flange2VecOpposite.start.y + flange2EndPoint.y
                );

                sideSegment.start = this.center;
                var sideSegmentVec = this.vecStart.getPerpendicularVectorAtPoint(this.center);
                var sideSegmentEndPoint = getPointByVectorAndLength(sideSegmentVec, this.sideLength);

                sideSegment.end = this.side;

                var flange3Vec = sideSegmentVec.getPerpendicularVectorAtPoint(sideSegment.end!);
                var flange3VecOpposite = sideSegmentVec.getOppositeToPerpendicularVectorAtPoint(sideSegment.end!);

                var flange3StartPoint = getPointByVectorAndLength(flange3VecOpposite, this.flangeLength / 2);
                flange3Segment.start = new Point(
                    flange3Vec.start.x + flange3StartPoint.x,
                    flange3Vec.start.y + flange3StartPoint.y
                );
                var flange3EndPoint = getPointByVectorAndLength(flange3Vec, this.flangeLength / 2);
                flange3Segment.end = new Point(
                    flange3VecOpposite.start.x + flange3EndPoint.x,
                    flange3VecOpposite.start.y + flange3EndPoint.y
                );

            }



        }

    }


    clone() {
        return new Tee(this.shape, this.exhaustOrSupply, this.passOrTurn, this.widthCollectorMillimeter, this.heightCollectorMillimeter, this.widthPassMillimeter, this.heightPassMillimeter, this.widthTurnMillimeter, this.heightTurnMillimeter, this.flowRateCollectorCmh, this.flowRateTurnCmh, this.angleDegree, null, this._segmentBase, this._segmentCurrent);
    }

    getOnPassResistance() {
        // вытяжной на проход
        if (this.exhaustOrSupply === ObjectTypes.exhaust) {
            if (this.shape === ObjectTypes.rect) {

                const resistance = TeeExhaustRectangular.onPassResistance(
                    this.flowRateCollectorCmh,
                    this.flowRateTurnCmh,
                    this.widthCollectorMillimeter,
                    this.heightCollectorMillimeter,
                    this.widthTurnMillimeter,
                    this.heightTurnMillimeter,
                    this.widthPassMillimeter,
                    this.heightPassMillimeter,
                    this.angleDegree
                );

                return resistance;
            }
            if (this.shape === ObjectTypes.round) {

                var resistance = TeeExhaustRound.onPassResistance(
                    this.flowRateCollectorCmh,
                    this.flowRateTurnCmh,
                    this.widthCollectorMillimeter,
                    this.heightCollectorMillimeter,
                    this.widthTurnMillimeter,
                    this.heightTurnMillimeter,
                    this.widthPassMillimeter,
                    this.heightPassMillimeter,
                    this.angleDegree
                );

                return resistance;
            }
        }

        // приточный на проход
        if (this.exhaustOrSupply === ObjectTypes.supply) {
            if (this.shape === ObjectTypes.rect) {

                var resistance = TeeSupplyRectangular.onPassResistance(
                    this.flowRateCollectorCmh,
                    this.flowRateTurnCmh,
                    this.widthCollectorMillimeter,
                    this.heightCollectorMillimeter,
                    this.widthTurnMillimeter,
                    this.heightTurnMillimeter,
                    this.widthPassMillimeter,
                    this.heightPassMillimeter,
                    this.angleDegree
                );

                return resistance;
            }
            if (this.shape === ObjectTypes.round) {

                var resistance = TeeSupplyRound.onPassResistance(
                    this.flowRateCollectorCmh,
                    this.flowRateTurnCmh,
                    this.widthCollectorMillimeter,
                    this.heightCollectorMillimeter,
                    this.widthTurnMillimeter,
                    this.heightTurnMillimeter,
                    this.widthPassMillimeter,
                    this.heightPassMillimeter,
                    this.angleDegree
                );

                return resistance;
            }
        }
    }
    getOnTurnResistance() {
        // вытяжной на поворот
        if (this.exhaustOrSupply === ObjectTypes.exhaust) {
            if (this.shape === ObjectTypes.rect) {

                var resistance = TeeExhaustRectangular.toTurnResistance(
                    this.flowRateCollectorCmh,
                    this.flowRateTurnCmh,
                    this.widthCollectorMillimeter,
                    this.heightCollectorMillimeter,
                    this.widthTurnMillimeter,
                    this.heightTurnMillimeter,
                    this.widthPassMillimeter,
                    this.heightPassMillimeter,
                    this.angleDegree
                )

                return resistance;
            }
            if (this.shape === ObjectTypes.round) {
                var resistance = TeeExhaustRound.toTurnResistance(
                    this.flowRateCollectorCmh,
                    this.flowRateTurnCmh,
                    this.widthCollectorMillimeter,
                    this.heightCollectorMillimeter,
                    this.widthTurnMillimeter,
                    this.heightTurnMillimeter,
                    this.widthPassMillimeter,
                    this.heightPassMillimeter,
                    this.angleDegree
                );

                return resistance;
            }
        }
        // приточный на поворот
        if (this.exhaustOrSupply === ObjectTypes.supply) {
            if (this.shape === ObjectTypes.rect) {

                var resistance = TeeSupplyRectangular.toTurnResistance(
                    this.flowRateCollectorCmh,
                    this.flowRateTurnCmh,
                    this.widthCollectorMillimeter,
                    this.heightCollectorMillimeter,
                    this.widthTurnMillimeter,
                    this.heightTurnMillimeter,
                    this.widthPassMillimeter,
                    this.heightPassMillimeter,
                    this.angleDegree
                );


                return resistance;
            }
            if (this.shape === ObjectTypes.round) {
                var resistance = TeeSupplyRound.toTurnResistance(
                    this.flowRateCollectorCmh,
                    this.flowRateTurnCmh,
                    this.widthCollectorMillimeter,
                    this.heightCollectorMillimeter,
                    this.widthTurnMillimeter,
                    this.heightTurnMillimeter,
                    this.widthPassMillimeter,
                    this.heightPassMillimeter,
                    this.angleDegree
                );

                return resistance;
            }
        }
    }

    isinSelectFrame(frame:AbstractFrame) {
        if (!this.start) {
            throw new Error('## Start point is not set');
        }
        if (!this.end) {
            throw new Error('## End point is not set');
        }
        const angle = Math.atan2(this.end.y - this.start.y, this.end.x - this.start.x);
        const offsetX = s.tolerance * Math.sin(angle);
        const offsetY = s.tolerance * Math.cos(angle);
        const point1 = new Point(this.start.x - offsetX, this.start.y + offsetY);
        const point2 = new Point(this.end.x - offsetX, this.end.y + offsetY);
        const point3 = new Point(this.end.x + offsetX, this.end.y - offsetY);
        const point4 = new Point(this.start.x + offsetX, this.start.y - offsetY);
        if (
            isPointInsideFrame(frame, point1.x, point1.y) &&
            isPointInsideFrame(frame, point2.x, point2.y) &&
            isPointInsideFrame(frame, point3.x, point3.y) &&
            isPointInsideFrame(frame, point4.x, point4.y)
        ) {
            return true;
        }

        return false;
    }

    isinSelectFrameAtLeast(frame:AbstractFrame) {

        var isinFrame = this.isinSelectFrame(frame);

        if (!frame.vec) {
            throw new Error('## Frame vector is not set');
        }

        var vec1 = frame.vec.getOrtho1X();
        var vec2 = frame.vec.getOrtho2X();
        var vec3 = frame.vec.getOrtho1Y();
        var vec4 = frame.vec.getOrtho2Y();

        var crossx1 = findLinesIntersection(this as unknown as Line, vec1 as unknown as Line);
        var crossx2 = findLinesIntersection(this as unknown as Line, vec2 as unknown as Line);
        var crossy1 = findLinesIntersection(this as unknown as Line, vec3 as unknown as Line);
        var crossy2 = findLinesIntersection(this as unknown as Line, vec4 as unknown as Line);

        if (crossx1 || crossx2 || crossy1 || crossy2 || isinFrame) {
            return true;
        }
        return false;


    }

    override zoom(zl:number) {
        this.shapes.forEach(s => {
            s.zoom(zl);
        });

        this.cpoints.forEach(p => {
            p.zoom(zl);
        });

        // Zoom crucial points (_base1, _base2, _side) used for route detection and grip markers
        var zoom_mat = mat3.fromScaling(mat3.create(), new Float32Array([zl, zl, 1]));
        if (this._base1) {
            const newBase1 = transformPointByMatrix3(zoom_mat, this._base1);
            this._base1.x = newBase1.x;
            this._base1.y = newBase1.y;
        }
        if (this._base2) {
            const newBase2 = transformPointByMatrix3(zoom_mat, this._base2);
            this._base2.x = newBase2.x;
            this._base2.y = newBase2.y;
        }
        if (this._side) {
            const newSide = transformPointByMatrix3(zoom_mat, this._side);
            this._side.x = newSide.x;
            this._side.y = newSide.y;
        }

        this.updateSavedVertices();

    }

    override pan(tx:number, ty:number) {
        this.shapes.forEach(s => {
            s.pan(tx, ty);
        });

        this.cpoints.forEach(p => {
            p.pan(tx, ty);
        });

        // Pan crucial points (_base1, _base2, _side) used for route detection and grip markers
        var pan_mat = mat3.fromTranslation(mat3.create(), new Float32Array([tx, ty, 0]));
        mat3.transpose(pan_mat, pan_mat);
        if (this._base1) {
            const newBase1 = transformPointByMatrix3(pan_mat, this._base1);
            this._base1.x = newBase1.x;
            this._base1.y = newBase1.y;
        }
        if (this._base2) {
            const newBase2 = transformPointByMatrix3(pan_mat, this._base2);
            this._base2.x = newBase2.x;
            this._base2.y = newBase2.y;
        }
        if (this._side) {
            const newSide = transformPointByMatrix3(pan_mat, this._side);
            this._side.x = newSide.x;
            this._side.y = newSide.y;
        }

        this.updateSavedVertices();

    }

    assignConnectionPoints() {
        if (!this._base1) {
            throw new Error('## Base 1 point is not set');
        }
        if (!this._base2) {
            throw new Error('## Base 2 point is not set');
        }
        if (!this._side) {
            throw new Error('## Side point is not set');
        }
        var cp1 = this._base1;
        var cp2 = this._base2;
        var cp3 = this._side;

        super.assignConnectionPoint(cp1);
        super.assignConnectionPoint(cp2);
        super.assignConnectionPoint(cp3);
    }

    /**
     * Функция возвращает объект с характерными точками, используется при определении маршутов в функции getSingleRoute
     * @returns {
     * {
     *  start: Point,
     *  end: Point,
     *  middle: Point
     * }
     */
    override getCrucialPoints(): RouteCrucialPoints {
        if (!this._base1) {
            throw new Error('## Base 1 point is not set');
        }
        if (!this._base2) {
            throw new Error('## Base 2 point is not set');
        }
        if (!this._side) {
            throw new Error('## Side point is not set');
        }
        return {
            start: this._base1,
            end: this._base2,
            mid: this._side,
            flowDirections: {
                start: FlowDirection.UNKNOWN,
                end: FlowDirection.UNKNOWN,
                mid: FlowDirection.UNKNOWN
            },
            isTerminal: {
                start: false,
                end: false,
                mid: false
            },
            pressure: {
                start: 0,
                end: 0,
                mid: 0
            }
        }
    }
}

function generateShapes(teeType: string) {
    return createTeeLines();
}
